import { useNavigate } from 'react-router-dom';
import { Target, BellOff } from 'lucide-react';
import type { GoalDue } from '@techsofit/api-types';
import { Card, Avatar, EmptyState, Skeleton, cn } from '@techsofit/ui';
import { PageHeader } from '../components/PageHeader';
import { ActionMenu, type ActionItem } from '../components/ActionMenu';
import { useAsync } from '../lib/useAsync';
import { getGoalsDue, snoozeGoal } from '../lib/gymApi';
import { valueLabel } from '../lib/goals';

const overdueLabel = (d: number) => (d <= 0 ? 'Due today' : d === 1 ? '1 day overdue' : `${d} days overdue`);

export function CheckInsPage() {
  const navigate = useNavigate();
  const { data, loading, reload } = useAsync(getGoalsDue);
  const due = data ?? [];

  async function snooze(goalId: string, days: number) {
    await snoozeGoal(goalId, days);
    reload();
  }

  function actionsFor(g: GoalDue): ActionItem[] {
    return [
      { label: 'Snooze 2 days', icon: <BellOff className="size-4" aria-hidden />, onClick: () => snooze(g.goalId, 2) },
      { label: 'Snooze 1 week', icon: <BellOff className="size-4" aria-hidden />, onClick: () => snooze(g.goalId, 7) },
      { label: 'Open goal', icon: <Target className="size-4" aria-hidden />, onClick: () => navigate(`/goals/${g.goalId}`) },
    ];
  }

  return (
    <section className="animate-fade-up">
      <PageHeader
        eyebrow={due.length ? `${due.length} due` : 'All caught up'}
        title="Check-ins due"
        description="Members whose goal measurement is due. They're reminded automatically — snooze if you’ve just seen them."
      />

      <Card className="overflow-hidden p-0">
        {loading ? (
          <div className="flex flex-col gap-2 p-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-14" />
            ))}
          </div>
        ) : due.length ? (
          <ul className="divide-y divide-line">
            {due.map((g) => (
              <li key={g.goalId} className="flex items-center gap-3 px-4 py-3 md:px-5">
                <Avatar name={g.memberName} size="sm" />
                <button onClick={() => navigate(`/goals/${g.goalId}`)} className="min-w-0 flex-1 text-left">
                  <p className="truncate font-medium text-ink">{g.memberName}</p>
                  <p className="truncate text-sm text-muted">
                    {g.title} · {valueLabel(g.currentValue, g.unit)}
                    {g.targetValue != null && ` → ${valueLabel(g.targetValue, g.unit)}`}
                  </p>
                </button>
                <span className={cn('hidden shrink-0 rounded-pill px-2.5 py-1 text-xs font-medium sm:inline',
                  g.daysOverdue > 0 ? 'bg-warn-soft text-warn-ink' : 'bg-surface text-ink-soft')}>
                  {overdueLabel(g.daysOverdue)}
                </span>
                <ActionMenu items={actionsFor(g)} label={`Actions for ${g.memberName}`} />
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            icon={<Target className="size-6" aria-hidden />}
            title="All caught up"
            description="No goal check-ins are due right now."
            className="rounded-none shadow-none"
          />
        )}
      </Card>
    </section>
  );
}
